import { Component, inject, signal } from '@angular/core';
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UrlService } from '../../services/url.service';
import { finalize } from 'rxjs';
import { ToastService } from '../../../shared/services/toast.service';
import { LoadingButtonComponent } from "../../../shared/components/loading-button/loading-button.component";

@Component({
  selector: 'app-verify-url-modal',
  imports: [FormsModule, ReactiveFormsModule, LoadingButtonComponent],
  template: `
    <div class="modal-header">
      <h4 class="modal-title" i18n>Verifica il dominio</h4>
      <button type="button" class="btn-close" aria-label="Close" (click)="activeModal.dismiss()"></button>
    </div>
    <div class="modal-body p-3">
        <form [formGroup]="verifyUrlForm" class="d-flex align-items-end gap-3 p-4 mb-3 rounded bg-light">
          <div class="w-100">
            <label for="identifierUrl" class="form-label mb-0" i18n>Inserisci URL del tuo sito</label>
            <input type="text" class="form-control flex-grow-1" id="identifierUrl" formControlName="identifierUrl" i18n-aria-label aria-label="input url">
          </div>
        </form>
        <h6 class="fw-bold mb-3">1. Conferma la proprietà del dominio</h6>
        <p class="mb-3">
          Clicca su "Genera File" per scaricare un file HTML di verifica. Il file conterrà un token univoco che serve a confermare la proprietà del dominio.
        </p>
        <div class="d-flex justify-content-center mb-3">
          <app-loading-button [loading]="genTokenloading" [disabled]="genTokenloading || verifyUrlForm.invalid" (clickButton)="onSubmit()" i18n>Genera File</app-loading-button>
        </div>
        <p class="mb-3">
          Un volta scaricato il file, caricalo nella folder del tuo host e passa al punto 2 per verificare il sito web.
        </p>
        <h6 class="fw-bold mb-3">2. Hai già caricato il file sul tuo host?</h6>
        <p class="mb-3">
          Clicca su "Verifica Sito Web" per confermare la proprietà del dominio. Se il file è stato caricato correttamente, riceverai una notifica di successo e potrai chiudere la finestra di verifica.
        </p>
        <div class="d-flex justify-content-center">
          <app-loading-button [loading]="verifyLoading" type="button" [disabled]="verifyLoading || verifyUrlForm.invalid" (clickButton)="recheckUrl()" i18n>
            Verifica Sito Web
          </app-loading-button>
        </div>
        @if (errorMessage()) {
        <div class="alert alert-danger mt-3 rounded-3">
          <p class="fw-bold">Si è verificato un errore durante l’elaborazione del file.</p>
          <p>Ti invitiamo a verificare che:</p>
          <ol>
            <li>Il file sia effettivamente raggiungibile e accessibile.</li>
            <li>
              Non siano presenti restrizioni o regole di sicurezza che ne impediscano l’accesso
              (es. firewall, WAF, permessi, policy di rete).
            </li>
          </ol>
          <p>Dopo aver effettuato le verifiche, riprova tra qualche ora.</p>
          <p>
            Se il problema persiste, contatta il supporto fornendo i dettagli dell’operazione eseguita.
          </p>
        </div>
        }
    </div>
  `
})
export class VerifyUrlModalComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly urlSrv = inject(UrlService);
  private readonly toastSrv = inject(ToastService);
  activeModal = inject(NgbActiveModal);

  genTokenloading = false;
  verifyLoading = false;

  errorMessage = signal<string>('');

  verifyUrlForm: FormGroup = this.fb.group(
    {
      identifierUrl: [undefined, [Validators.required, Validators.minLength(4)]],
    }
  );

  onSubmit(): void {
    this.genTokenloading = true;
    const identifierUrl = this.verifyUrlForm.controls['identifierUrl'].getRawValue();
    this.urlSrv.createVerificationToken(identifierUrl)
      .pipe(finalize(() => this.genTokenloading = false))
      .subscribe({
        next: (response) => {
          this.downloadHtmlFile(response.token);
        }
      });
  }

  private downloadHtmlFile(token: string) {
    const htmlContent = `google-site-verification: ${token}`;

    // Creare un Blob con il contenuto HTML
    const blob = new Blob([htmlContent], { type: 'text/html' });

    // Creare un URL per il blob
    const url = window.URL.createObjectURL(blob);

    // Creare un link per il download
    const a = document.createElement('a');
    a.href = url;
    a.download = token; // Nome del file

    // Simulare il clic per avviare il download
    document.body.appendChild(a);
    a.click();

    // Pulire l'URL creato
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  recheckUrl() {
    this.verifyUrlForm.disable();
    this.verifyLoading = true;
    const identifierUrl = this.verifyUrlForm.controls['identifierUrl'].getRawValue();
    this.urlSrv.insertVerificationToken(identifierUrl)
      .pipe(finalize(() => this.verifyLoading = false))
      .subscribe({
        next: () => {
          this.toastSrv.show({ type: 'success', message: $localize`Url validata con successo` });
          this.activeModal.close();
        },
        error: (error) => {
          this.errorMessage.set($localize`Errore durante la validazione del sito web, verifica che il file sia stato caricato correttamente nella root folder del tuo host e riprova.`);
          this.verifyUrlForm.enable();
        }
      });
    // 
  }

}
